import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MdContentCopy, MdDownload } from "react-icons/md";
import { NumericFormat } from "react-number-format";
import { QRCodeCanvas } from "qrcode.react";
import { factory } from "../../../../../api/apiFactory";
import { showNotification } from "../../../../../app/slices/notificationSlice";

const qiTransferApi = factory.get("qiTransfer");

// "تسديد عبر QI": creates a كي (Tasdid) bill for the POS whose wallet is
// open, then shows the payment number to pay it with.
const QiPayButton = ({ userId, onCreated }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [sending, setSending] = useState(false);
  const [created, setCreated] = useState(null);
  const qrRef = useRef(null);
  const { user_roles, permissions } = useSelector(
    (state) => state.user.userInfo,
  );

  const notify = (message, type) =>
    dispatch(showNotification({ message, type }));

  const close = () => {
    setOpen(false);
    setAmount("");
    setCreated(null);
  };

  const downloadQr = () => {
    const link = document.createElement("a");
    link.href = qrRef.current.toDataURL("image/png");
    link.download = `qi-${created.payment_id}.png`;
    link.click();
  };

  const errorOf = (data) =>
    data?.errors?.message ??
    data?.errors?.[0] ??
    data?.message ??
    "An error occurred";

  const submit = async () => {
    if (!(Number(amount) > 0)) return notify("يرجى إدخال مبلغ صحيح", "error");
    setSending(true);
    try {
      const { data } = await qiTransferApi.create({
        amount: Number(amount),
        user_id: userId,
      });
      // The backend can answer 200 with status false (e.g. a Tasdid failure).
      if (data?.status === false) return notify(errorOf(data), "error");
      setCreated(data?.data?.qi_transfer ?? {});
      onCreated?.();
      notify(data?.message || "تم انشاء الفاتورة", "success");
    } catch (err) {
      notify(errorOf(err?.response?.data), "error");
    } finally {
      setSending(false);
    }
  };

  const canCreate =
    user_roles.includes("super_admin") ||
    permissions.some((p) => p.name === "qi_transfer_create");
  if (!canCreate) return null;

  return (
    <>
      <Button
        size="small"
        variant="contained"
        fullWidth
        onClick={() => setOpen(true)}
      >
        تسديد عبر QI
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="xs" dir="rtl">
        <DialogTitle>تسديد عبر QI</DialogTitle>
        <DialogContent>
          {created ? (
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Typography>
                المبلغ: {Number(created.amount ?? amount).toLocaleString()} IQD
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ادفع الفاتورة من تطبيق كي باستخدام رقم الدفع
              </Typography>
              <Stack direction="row" sx={{ alignItems: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {created.payment_id ?? "-"}
                </Typography>
                {created.payment_id && (
                  <IconButton
                    onClick={() =>
                      navigator.clipboard
                        .writeText(String(created.payment_id))
                        .then(() => notify("تم النسخ", "success"))
                    }
                  >
                    <MdContentCopy />
                  </IconButton>
                )}
              </Stack>
              {created.payment_id && (
                <Stack spacing={1} sx={{ alignItems: "center", pt: 1 }}>
                  <QRCodeCanvas
                    ref={qrRef}
                    value={String(created.payment_id)}
                    size={220}
                    marginSize={2}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MdDownload />}
                    onClick={downloadQr}
                  >
                    تحميل QR
                  </Button>
                </Stack>
              )}
            </Stack>
          ) : (
            <NumericFormat
              customInput={TextField}
              autoFocus
              size="small"
              label="المبلغ"
              fullWidth
              sx={{ mt: 1 }}
              thousandSeparator
              valueIsNumericString
              value={amount}
              onValueChange={(values) => setAmount(values.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">IQD</InputAdornment>
                ),
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>{created ? "اغلاق" : "الغاء"}</Button>
          {!created && (
            <Button variant="contained" onClick={submit} disabled={sending}>
              انشاء فاتورة
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QiPayButton;
